import * as ChannelService from '@channel.io/channel-web-sdk-loader';
import { useLang } from '@inflearn/i18n-tools';
import { isNil } from 'lodash-es';
import { useEffect } from 'react';

import {
  NEXT_PUBLIC_ADMIN_HOST,
  NEXT_PUBLIC_CHANNEL_PLUGIN_KEY,
  NEXT_PUBLIC_ENV,
  NEXT_PUBLIC_INFLEARN_HOST,
} from '../../appConfig';
import {
  API_ENDPOINT,
  useClientApiV1UserStatisticsQuery,
} from '../../resources/@types/courseSchema';
import { INFLEARN_B2B_URL, MY_URL } from '../constants/urls';
import { supportBotAssignmentKey, supportBotProfile } from '../lib/channelTalk/supportBotCanary';
import useClientAccountProfileQuery from '../queries/useClientAccountProfileQuery';

/** ChannelTalk은 운영환경에서만 노출되어야 함 */
const isProduction = NEXT_PUBLIC_ENV === 'production';

export const useChannelTalk = () => {
  const { lang } = useLang();
  const { data: userStatistics, isLoading: isLoadingUserStatistics } =
    useClientApiV1UserStatisticsQuery({
      queryKey: [API_ENDPOINT.CLIENT_V1_USER_STATISTICS],
      url: `${API_ENDPOINT.CLIENT_V1_USER_STATISTICS}?withCache=false`,
      options: {
        enabled: isProduction,
      },
    });

  const profileQuery = useClientAccountProfileQuery({ enabled: isProduction });
  const isCanaryEligible = Boolean(
    profileQuery.isSuccess &&
      lang === 'ko' &&
      (!profileQuery.data || (!profileQuery.data.group && !profileQuery.data.managedGroup)),
  );
  const assignmentKey = supportBotAssignmentKey(profileQuery.data?.id);

  useEffect(() => {
    if (!isProduction) {
      return;
    }

    ChannelService.loadScript();

    if (isLoadingUserStatistics || profileQuery.isLoading) {
      return;
    }

    if (isNil(profileQuery.data)) {
      ChannelService.boot({
        pluginKey: NEXT_PUBLIC_CHANNEL_PLUGIN_KEY,
        profile: supportBotProfile(isCanaryEligible, assignmentKey),
      });

      return;
    }
    const userId = profileQuery.data?.id;

    ChannelService.boot({
      pluginKey: NEXT_PUBLIC_CHANNEL_PLUGIN_KEY,
      /**
       * [주의]
       * 로컬에서 테스트해야 한다면 운영데이터와 겹치지 않도록 특이한 문자열을 전달해주어야합니다. (ex. inflab:development:test)
       * 또한 테스트 후 memberId를 꼭 userId.toString()으로 원복시켜주어야 합니다.
       */
      memberId: userId.toString(),
      profile: {
        ...supportBotProfile(isCanaryEligible, assignmentKey),
        name: profileQuery.data?.name || '정보 없음',
        email: profileQuery.data?.email || '정보 없음',
        order_list: userId ? `${MY_URL.ORDERS()}?userId=${userId}` : '미 로그인',
        ...(userStatistics
          ? {
              purcharseIds: userStatistics.userCourses,
              totalQuantity: userStatistics.userCourseCnt,
              totalPayPrice: userStatistics.userCourseTotalPaymentPrice,
            }
          : {}),
        is_instructor: profileQuery.data?.isInstructor ? '강사' : '학생',
        isReactNative: 'Web',
        user_dashboard: userId
          ? `${NEXT_PUBLIC_INFLEARN_HOST}/users/${userId}/dashboard`
          : '미 로그인',
        user_my_courses: userId ? `${MY_URL.COURSES()}?user_id=${userId}` : '미 로그인',
        isAdmin: !!profileQuery.data?.isAdmin,
        allowedMarketing: profileQuery.data?.notification.isAllowMarketing,
        allowedNewsEmail: profileQuery.data?.notification.isAllowNewsEmail,
        createdAt: profileQuery.data?.joinedAt,
        inactiveAt: profileQuery.data?.inactiveAt ?? '',
        B2BMember: profileQuery.data?.group
          ? `${profileQuery.data?.group.id} - ${profileQuery.data?.group.name}`
          : 'NOT', // groupName 추가
        B2BGroupName: profileQuery.data?.group ? (profileQuery.data?.group.id ?? 'NOT') : 'NOT',
        B2BManger: profileQuery.data?.managedGroup
          ? `${profileQuery.data?.managedGroup?.id} - ${profileQuery.data?.managedGroup?.name}`
          : 'NOT',
        B2BMemberPage: (() => {
          if (profileQuery.data?.managedGroup) {
            return '매니저';
          }

          if (profileQuery.data?.group) {
            return `${INFLEARN_B2B_URL.GROUP_MEMBERS(profileQuery.data?.group.id)}`;
          }

          return '일반 회원';
        })(),
        B2BMangerPage: (() => {
          if (profileQuery.data?.managedGroup) {
            return `${INFLEARN_B2B_URL.GROUP_MEMBERS(profileQuery.data?.managedGroup.id)}`;
          }

          if (profileQuery.data?.group) {
            return '멤버';
          }

          return '일반 회원';
        })(),
        userColorType: profileQuery.data.isBlocked ? '블랙' : '-',
        adminCourseManagePageLink:
          userId && profileQuery.data.isInstructor
            ? `${NEXT_PUBLIC_ADMIN_HOST}/admin/courses?s_type=instructor&s=${profileQuery.data?.email}`
            : '정보 없음',
      },
    });
  }, [
    profileQuery.data,
    userStatistics,
    isLoadingUserStatistics,
    profileQuery.isLoading,
    assignmentKey,
    isCanaryEligible,
  ]);

  const showButton = () => {
    if (!isProduction) {
      return;
    }

    ChannelService.showChannelButton();
  };

  const hideButton = () => {
    if (!isProduction) {
      return;
    }

    ChannelService.hideChannelButton();
  };

  return { showButton, hideButton };
};
